import React, { useEffect, useMemo, useState } from "react";
import { ScrollView, View, StyleSheet, Pressable, TextInput } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import { locationService } from "@/services/locationService";
import { LocationType } from "@/types/db/locations";
import { supabase } from "@/lib/supabase-client";
import useAuth from "@/hooks/useAuth";
import useToast from "@/hooks/useToast";
import { getEntityPermissions } from "@/lib/permissions";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useTheme } from "@/hooks/useTheme";
import { router } from "expo-router";
import Badge from "@/components/ui/Badge";
import { useSettingsSearch } from "@/contexts/SettingsSearchContext";

export default function LocationsSettingsPage() {
  const { userRoles, loading: authLoading } = useAuth();
  const { colors } = useTheme();
  const toast = useToast();
  const [locations, setLocations] = useState<LocationType[]>([]);
  const [loading, setLoading] = useState(true);
  const [marketMap, setMarketMap] = useState<Record<string, string>>({});
  const { searchValue, setSearchValue, searchFocused, setSearchFocused } = useSettingsSearch("locations");

  const filteredLocations = useMemo(() => {
    if (!searchValue.trim()) return locations;
    const search = searchValue.toLowerCase();
    return locations.filter(
      (item) =>
        item.name?.toLowerCase().includes(search) ||
        item.city?.toLowerCase().includes(search) ||
        item.state?.toLowerCase().includes(search) ||
        item.street?.toLowerCase().includes(search),
    );
  }, [locations, searchValue]);

  // Get permissions for locations - using "locations" as entity type
  const permissions = getEntityPermissions(userRoles, "locations");

  // Fetch markets for display mapping
  useEffect(() => {
    (async () => {
      const { data } = await supabase.schema("lists").from("markets").select("id, name");
      if (data) {
        setMarketMap(Object.fromEntries(data.map((m) => [m.id, m.name])));
      }
    })();
  }, []);

  const fetchLocations = async () => {
    setLoading(true);
    try {
      const data = await locationService.getLocations();
      setLocations(data);
    } catch (error: any) {
      toast.showToast({
        title: "Failed to load locations",
        message: error?.message || "Unable to fetch locations",
        toastType: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Don't fetch if auth is still loading
    if (authLoading) return;

    if (permissions.read) {
      fetchLocations();
    } else {
      // If no read permission, stop loading
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissions.read, authLoading]);

  const handleDeleteLocation = async (locationId: string) => {
    try {
      await locationService.deleteLocationsByIds([locationId]);
      setLocations(locations.filter((l) => l.id !== locationId));

      toast.showToast({
        title: "Location deleted",
        message: "Location deleted successfully",
        toastType: "success",
      });
    } catch (error: any) {
      toast.showToast({
        title: "Failed to delete location",
        message: error?.message || "Unable to delete location",
        toastType: "error",
      });
    }
  };

  // If no read permission, show error
  if (!permissions.read) {
    return (
      <ScrollView contentContainerStyle={styles.centered}>
        <MaterialCommunityIcons name="lock" size={48} color={colors.textSecondary} />
        <ThemedText type="defaultSemiBold" style={{ marginTop: 16 }}>
          Access Denied
        </ThemedText>
        <ThemedText style={{ opacity: 0.7, marginTop: 8 }}>You don&apos;t have permission to view locations</ThemedText>
      </ScrollView>
    );
  }

  if (authLoading || loading) {
    return (
      <ScrollView contentContainerStyle={styles.centered}>
        <ThemedText>{authLoading ? "Loading authentication..." : "Loading locations..."}</ThemedText>
      </ScrollView>
    );
  }

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.headerTop}>
          <View>
            <ThemedText type="title">Locations Management</ThemedText>
            <ThemedText style={{ opacity: 0.7, marginTop: 4 }}>Manage your locations and banned users</ThemedText>
          </View>

          {permissions.create && (
            <Button
              text="New Location"
              onPress={() => router.push("/settings/locations/new")}
              style={[styles.addButton, { backgroundColor: colors.green.background }]}
              textStyle={{ color: "white" }}
            />
          )}
        </View>

        <View style={styles.searchRow}>
          <View style={[styles.searchContainer, { borderBottomColor: searchFocused ? colors.primary : colors.border }]}>
            <MaterialCommunityIcons
              name="magnify"
              size={20}
              color={searchFocused ? colors.primary : colors.textSecondary}
            />
            <TextInput
              value={searchValue}
              onChangeText={setSearchValue}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setSearchFocused(false)}
              placeholder="Search..."
              placeholderTextColor={colors.textSecondary}
              style={[styles.searchInput, { color: colors.text, outlineStyle: "none" } as any]}
            />
            {searchValue.length > 0 && (
              <Pressable onPress={() => setSearchValue("")}>
                <MaterialCommunityIcons name="close-circle" size={18} color={colors.textSecondary} />
              </Pressable>
            )}
          </View>
        </View>
      </View>

      {/* Locations List */}
      <ScrollView>
        {locations.length === 0 ? (
          <View style={styles.emptyState}>
            <MaterialCommunityIcons name="map-marker-outline" size={64} color={colors.textSecondary} />
            <ThemedText style={{ marginTop: 16, opacity: 0.7 }}>No locations configured</ThemedText>
            {permissions.create && (
              <Button
                text="Create First Location"
                onPress={() => router.push("/settings/locations/new")}
                style={{ marginTop: 16 }}
              />
            )}
          </View>
        ) : (
          <View style={styles.locationsList}>
            {filteredLocations.map((location) => {
              const bannedCount = (location as any).banned_users?.length || 0;

              return (
                <Pressable
                  key={location.id}
                  style={[
                    styles.locationCard,
                    {
                      backgroundColor: colors.card,
                      borderColor: colors.border,
                    },
                  ]}
                  onPress={() => router.push(`/settings/locations/${location.id}`)}
                >
                  <View style={styles.locationHeader}>
                    <View style={styles.titleRow}>
                      <ThemedText type="defaultSemiBold" style={{ fontSize: 16 }}>
                        {location.name}
                      </ThemedText>
                      {location.instructions && (
                        <Badge
                          coloredLabel
                          label="instructions"
                          icon="information-outline"
                          text={location.instructions}
                          tone="amber"
                        />
                      )}
                      {(location as any).market_id && marketMap[(location as any).market_id] && (
                        <Badge
                          coloredLabel
                          label="market"
                          icon="map-marker"
                          text={marketMap[(location as any).market_id]}
                          tone="blue"
                        />
                      )}
                    </View>
                    {permissions.delete && (
                      <Pressable
                        style={styles.menuButton}
                        onPress={(e) => {
                          e.stopPropagation();
                          handleDeleteLocation(location.id);
                        }}
                      >
                        <MaterialCommunityIcons name="delete" size={20} color={colors.red?.text || colors.text} />
                      </Pressable>
                    )}
                  </View>

                  <ThemedText style={{ opacity: 0.6, marginTop: 4, fontSize: 12 }}>
                    {location.street}, {location.city}, {location.state} {location.zip}
                  </ThemedText>
                  {bannedCount > 0 && (
                    <ThemedText style={{ opacity: 0.6, marginTop: 2, fontSize: 11 }}>
                      {bannedCount} banned user{bannedCount !== 1 ? "s" : ""}
                    </ThemedText>
                  )}

                  {location.description && (
                    <ThemedText style={{ fontSize: 12, opacity: 0.7, marginTop: 8 }}>{location.description}</ThemedText>
                  )}
                </Pressable>
              );
            })}
          </View>
        )}
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  centered: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
  },
  header: {
    gap: 12,
    paddingBottom: 16,
  },
  headerTop: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 16,
  },
  searchRow: {
    flexDirection: "row",
    justifyContent: "flex-end",
  },
  searchContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingVertical: 6,
    borderBottomWidth: 1,
    width: 200,
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    padding: 0,
  },
  addButton: {
    paddingHorizontal: 8,
    height: 35,
  },
  emptyState: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 48,
  },
  locationsList: {
    gap: 12,
  },
  locationCard: {
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
  },
  locationHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  titleRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    flex: 1,
    flexWrap: "wrap",
  },
  locationInfo: {
    flex: 1,
  },
  badgeRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 8,
  },
  menuButton: {
    padding: 4,
  },
  instructionsBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
    borderWidth: 1,
    marginTop: 8,
    alignSelf: "flex-start",
  },
  instructionsText: {
    fontSize: 11,
    fontWeight: "600",
  },
});
